import { Flex, Text, Badge, Box, Separator } from "@radix-ui/themes";
import { useCallback } from "react";
import { ReleaseConversation } from "@/v2/entities/release-conversation/ReleaseConversation.model";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import { DateFormatter } from "@/v2/components/formatters/DateFormatter";
import { FeedbackFormatter } from "@/v2/components/formatters/FeedbackFormatter";
import { Comment01Icon } from "@/v2/assets/icons/Comment01Icon";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";

interface SessionMetadataComponentsProps {
  session: ReleaseConversation;
  releasesWithVersions: Array<{ id: number; version: number }> | null;
  topics: Array<{ id: number; topic: string }> | null;
  showCreatedAt?: boolean;
  showTopic?: boolean;
  showVersion?: boolean;
  showFeedback?: boolean;
  showPendingReview?: boolean;
  showSubmitter?: boolean;
  showInteractionType?: boolean;
}

export const InteractionTypeBadge = ({
  session,
}: {
  session: ReleaseConversation;
}) => {
  const getInteractionType = useCallback(() => {
    if (session.ticketId || session.ticketInTrainingId) {
      return "tickets";
    }
    if (session.conversationId || session.conversationInTrainingId) {
      return "conversations";
    }
    return null;
  }, [session]);

  const interactionType = getInteractionType();

  if (interactionType === "tickets") {
    return <Ticket03Icon fill="blue" />;
  }
  if (interactionType === "conversations") {
    return <Comment01Icon fill="orange" />;
  }
  return null;
};

export const PendingReviewBadge = ({
  session,
}: {
  session: ReleaseConversation;
}) => {
  const isPendingReview = useCallback(() => {
    return !!(session.ticketInTrainingId || session.conversationInTrainingId);
  }, [session]);

  if (!isPendingReview()) return null;

  return (
    <Badge color="purple" variant="soft" size="1">
      Pending Review
    </Badge>
  );
};

export const SubmitterBadge = ({
  session,
}: {
  session: ReleaseConversation;
}) => {
  if (!session.quackUserId) return null;

  return (
    <Badge color="gray" variant="soft" size="1">
      {session.quackUserId}
    </Badge>
  );
};

export const FeedbackBadge = ({
  session,
}: {
  session: ReleaseConversation;
}) => {
  if (!session.feedback || session.feedback === "NONE") return null;

  return <FeedbackFormatter value={session.feedback} />;
};

export const VersionBadge = ({
  session,
  releasesWithVersions,
}: {
  session: ReleaseConversation;
  releasesWithVersions: Array<{ id: number; version: number }> | null;
}) => {
  const getVersion = useCallback(() => {
    const release = releasesWithVersions?.find(
      (r) => r.id === session.releaseId
    );
    return release ? `Version ${release.version}` : "Unknown Version";
  }, [session.releaseId, releasesWithVersions]);

  const version = getVersion();

  return (
    <Badge variant="soft" size="1">
      {version}
    </Badge>
  );
};

export const TopicBadge = ({
  session,
  topics,
}: {
  session: ReleaseConversation;
  topics: Array<{ id: number; topic: string }> | null;
}) => {
  const getTopic = useCallback(() => {
    const topic = topics?.find((t) => t.id === session.topicId);
    return topic?.topic || "Unknown Topic";
  }, [session.topicId, topics]);

  const topic = getTopic();

  return (
    <Badge variant="soft" size="1">
      {topic}
    </Badge>
  );
};

export const SessionMetadataHeader = ({
  session,
  releasesWithVersions,
  topics,
  showCreatedAt = true,
  showTopic = true,
  showVersion = true,
  showFeedback = true,
  showPendingReview = true,
  showSubmitter = true,
  showInteractionType = true,
}: SessionMetadataComponentsProps) => {
  return (
    <Box p="4" style={{ borderBottom: "1px solid var(--gray-4)" }}>
      <Flex direction="column" gap="3">
        <Flex justify="between" align="center">
          <Text size="2" weight="medium" color="gray" highContrast>
            #{session.id}
          </Text>
          {showInteractionType && (
            <Flex gap="2" align="center">
              <InteractionTypeBadge session={session} />
            </Flex>
          )}
        </Flex>

        <Flex gap="2" wrap="wrap" align="center">
          {showVersion && (
            <VersionBadge
              session={session}
              releasesWithVersions={releasesWithVersions}
            />
          )}
          {showTopic && <TopicBadge session={session} topics={topics} />}
          {showFeedback && <FeedbackBadge session={session} />}
          {showPendingReview && <PendingReviewBadge session={session} />}
          {showSubmitter && <SubmitterBadge session={session} />}
        </Flex>

        {showCreatedAt && session.createdAt && (
          <Text size="1" color="gray">
            Created: <DateFormatter value={session.createdAt} />
          </Text>
        )}
      </Flex>
    </Box>
  );
};

export const SessionMetadataInline = ({
  session,
  releasesWithVersions,
  topics,
  showTopic = true,
  showVersion = true,
  showFeedback = true,
  showPendingReview = true,
  showSubmitter = true,
  showInteractionType = true,
}: SessionMetadataComponentsProps) => {
  return (
    <>
      <Box p="2">
        <Flex gap="2" align="center" mb="1">
          <Text size="2" weight="medium" color="gray" highContrast>
            #{session.id}
          </Text>
          <Flex gap="2" wrap="wrap" align="center">
            {showInteractionType && <InteractionTypeBadge session={session} />}
          </Flex>
        </Flex>
        <Flex gap="2" wrap="wrap" align="center">
          {showVersion && (
            <VersionBadge
              session={session}
              releasesWithVersions={releasesWithVersions}
            />
          )}
          {showTopic && <TopicBadge session={session} topics={topics} />}
          {showFeedback && <FeedbackBadge session={session} />}
          {showPendingReview && <PendingReviewBadge session={session} />}
          {showSubmitter && <SubmitterBadge session={session} />}
        </Flex>
      </Box>
      <Separator size="4" color="gray" />
    </>
  );
};

export const GenericInteractionTypeBadge = ({
  interactionType,
}: {
  interactionType: ExploreInteractionType | null;
}) => {
  if (interactionType === "tickets") {
    return <Ticket03Icon fill="blue" />;
  }
  if (interactionType === "conversations") {
    return <Comment01Icon fill="orange" />;
  }
  return null;
};

export const GenericPendingReviewBadge = ({
  isPendingReview,
}: {
  isPendingReview: boolean;
}) => {
  if (!isPendingReview) return null;

  return (
    <Badge color="purple" variant="soft" size="1">
      Pending Review
    </Badge>
  );
};

export const GenericSubmitterBadge = ({
  submitter,
}: {
  submitter?: string;
}) => {
  if (!submitter) return null;

  return (
    <Badge color="gray" variant="soft" size="1">
      {submitter}
    </Badge>
  );
};

export const GenericFeedbackBadge = ({ category }: { category?: string }) => {
  if (!category || category === "NONE") return null;

  if (
    category === "POSITIVE" ||
    category === "NEGATIVE" ||
    category === "FORGOTTEN"
  ) {
    return <FeedbackFormatter value={category} />;
  }

  return (
    <Text size="1" color="gray" highContrast>
      {category}
    </Text>
  );
};

export const ExploreInteractionTypeBadge = ({
  interactionType,
}: {
  interactionType: ExploreInteractionType | null;
}) => {
  if (interactionType === "tickets") {
    return <Ticket03Icon fill="blue" />;
  }
  if (interactionType === "conversations") {
    return <Comment01Icon fill="orange" />;
  }
  return null;
};

export const ExplorePendingReviewBadge = ({
  isPendingReview,
}: {
  isPendingReview: boolean;
}) => {
  if (!isPendingReview) return null;

  return (
    <Badge color="purple" variant="soft" size="1">
      Pending Review
    </Badge>
  );
};

export const ExploreSubmitterBadge = ({
  submitter,
}: {
  submitter?: string;
}) => {
  if (!submitter) return null;

  return (
    <Badge color="gray" variant="soft" size="1">
      {submitter}
    </Badge>
  );
};

export const ExploreFeedbackBadge = ({ category }: { category?: string }) => {
  if (!category || category === "NONE") return null;

  if (category === "POSITIVE" || category === "NEGATIVE") {
    return <FeedbackFormatter value={category} />;
  }

  return (
    <Text size="1" color="gray" highContrast>
      {category}
    </Text>
  );
};
